import { useEffect } from "react";
import { Outlet, useOutletContext, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { ComplianceBreadcrumbs } from "../components/ComplianceBreadcrumbs";
import { Space } from "~/lib/types";
import {
  ExceptionGroup,
  ExceptionType,
  GetComplianceFrameworkQuery,
  UiAction,
  UiPage,
  useGetComplianceFrameworkQuery,
  useRegisterUserActionMutation,
} from "~/operations";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { PendingExceptionsTooltip } from "~/components/exceptions/pending-exceptions-tooltip";
import { CompletionV2 } from "~/components/Completion/CompletionV2";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { FrameworkHeader } from "~/pages/compliance/Framework/FrameworkHeader/FrameworkHeader";
import { setDocumentTitle } from "~/lib/commonUtils";

export type ComplianceFramework =
  GetComplianceFrameworkQuery["complianceFramework"];

type FrameworkContextType = {
  space: Space;
  framework: ComplianceFramework;
  exceptionGroups: ExceptionGroup[];
  isLoading: boolean;
};

export interface FrameworkProps {
  space: Space;
}

export function Framework({ space }: FrameworkProps) {
  const [searchParams] = useSearchParams();
  const frameworkMrn = searchParams.get("frameworkMrn") || "";
  const [registerUserAction] = useRegisterUserActionMutation();

  // Send user action mutation on mount
  useEffect(() => {
    registerUserAction({
      variables: {
        input: {
          action: UiAction.View,
          page: UiPage.ComplianceFramework,
          scopeMrn: space.mrn,
        },
      },
    }).then();
  }, []);

  const {
    data: { complianceFramework: framework } = {},
    error,
    loading,
  } = useGetComplianceFrameworkQuery({
    variables: {
      input: {
        frameworkMrn,
        scopeMrn: space.mrn,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    pendingExceptionsGroups,
    exceptionGroups,
    loadingListExceptionGroup,
  } = useFetchExceptions({
    mrn: frameworkMrn,
    scopeMrn: space.mrn,
    types: [ExceptionType.Compliance],
    includeDeleted: true,
  });

  setDocumentTitle([framework?.name, "Compliance", space.name]);

  if (loading && !framework) {
    return <LoadingPage what="compliance framework" />;
  }

  if (error || !framework) {
    return <LoadingFailedPage what="compliance framework" />;
  }

  const tabList: TabListItem[] = [
    {
      label: "Controls",
      to: `controls?spaceId=${space.id}&frameworkMrn=${frameworkMrn}`,
      route: "/controls",
    },
    {
      label: "Policies",
      to: `policies?spaceId=${space.id}&frameworkMrn=${frameworkMrn}`,
      route: "/policies",
    },
    {
      label: "Assets",
      to: `assets?spaceId=${space.id}&frameworkMrn=${frameworkMrn}`,
      route: "/assets",
    },
    {
      label: (
        <Box sx={{ display: "flex", alignItems: "center" }} gap={1}>
          Exceptions
          {pendingExceptionsGroups.length > 0 && <PendingExceptionsTooltip />}
        </Box>
      ),
      to: `exceptions?spaceId=${space.id}&frameworkMrn=${frameworkMrn}`,
      route: "/exceptions",
    },
  ].flatMap((tab) => (tab ? [tab] : []));

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "controls",
  );

  return (
    <Box>
      <ComplianceBreadcrumbs framework={framework} space={space} />
      <FrameworkHeader
        framework={framework}
        space={space}
        right={
          <CompletionV2
            progress={framework.stats.controls.averageCompletion}
            exceptionCount={framework.stats.controls.totalExceptions}
            outOfScopeCount={framework.stats.controls.outOfScopeControls}
          />
        }
      />
      <TabNavigation {...{ id: "space-settings-tabs", tabList, currentTab }} />
      <Box>
        <Outlet
          context={
            {
              space,
              framework,
              exceptionGroups,
              isLoading: loadingListExceptionGroup,
            } satisfies FrameworkContextType
          }
        />
      </Box>
    </Box>
  );
}

export function useFrameworkContext() {
  return useOutletContext<FrameworkContextType>();
}
